import { startCase } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';

import { MfaBackup, UserMfaDevice } from '@lib/api/entities';
import { SnUsersAPI } from '@lib/api/services';
import { SnCurrentUser } from '@lib/auth';
import { SnFormFieldConfig } from '@lib/form';
import { TranslationService } from '@lib/translation';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Component({
  templateUrl: './two-factor-authentication-dialog.component.html',
  styleUrls: ['./two-factor-authentication-dialog.component.scss'],
})
export class TwoFactorAuthenticationDialogComponent implements OnInit, OnDestroy {
  @ViewChild(MatStepper)
  public stepper: MatStepper;
  public isLinear = false;
  public passwordForm = new UntypedFormGroup({});
  public codeForm = new UntypedFormGroup({});
  public formModel: any = {};
  public mfa: UserMfaDevice;
  public backupCodes: MfaBackup;
  public qrCodeUrl: string;
  public copyContent;
  public isLoading: boolean = false;
  public isMfaConfirmed: boolean = false;

  public passwordFields: SnFormFieldConfig[] = [
    {
      key: 'name',
      type: 'select',
      templateOptions: this._translationService.createTranslatedFieldOptions(
          'common.appName',
          'account.dialogs.mfa.setup.appNamePlaceholder',
        {
        required: true,
        options: [
          { label: t('account.dialogs.mfa.setup.authy'), value: 'Authy' },
          { label: t('account.dialogs.mfa.setup.google'), value: 'Google Authenticator' },
          { label: t('account.dialogs.mfa.setup.lastPass'), value: 'LastPass Authenticator' },
          { label: t('account.dialogs.mfa.setup.microsoft'), value: 'Microsoft Authenticator' },
          { label: t('account.dialogs.mfa.setup.simple2fa'), value: 'Simple 2FA Authenticator' },
        ],
        }
      ),
    },
    {
      key: 'password',
      type: 'input',
      templateOptions: this._translationService.createTranslatedFieldOptions(
          'common.password',
          'common.enterPassword',
        {
        type: 'password',
        required: true,
        toggleMask: true,
        attributes: {
          autocomplete: 'new-password',
        },
        }
      ),
    },
  ];

  public codeField: SnFormFieldConfig[] = [
    {
      key: 'totpToken',
      type: 'input',
      name: 'uid',
      templateOptions: this._translationService.createTranslatedFieldOptions(
        'account.dialogs.mfa.setup.verificationStep.verificationToken',
        'account.dialogs.mfa.setup.verificationStep.verificationTokenPlaceholder',
        {
        appearance: 'outline',
        required: true,
        type: 'tel',
        }
      ),
    },
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _usersAPI: SnUsersAPI,
    private readonly _currentUser: SnCurrentUser,
    private readonly _dialogRef: MatDialogRef<TwoFactorAuthenticationDialogComponent>,
    private readonly _dialogService: SnDialogService,
    private readonly _translationService: TranslationService,
  ) { }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public isArray(value: any): boolean {
    return Array.isArray(value);
  }

  public toStartCase(text: string): string {
    return startCase(text);
  }

  public next(): void {
    this.stepper.next();
  }

  public previous(): void {
    this.stepper.previous();
  }

  public createMfa(): void {
    if (this.passwordForm.invalid) {
      return;
    }
    this.isLoading = true;
    this._usersAPI
      .createMfa(this._currentUser.id, {
        name: this.formModel.name,
        password: this.formModel.password,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (mfa) => {
          this.isLoading = false;
          this.mfa = mfa;
          this.qrCodeUrl = this.mfa.configUrl;
          this._checkBackupCodes();
          this.next();
        },
        error: (err) => {
          this.isLoading = false;
          this.passwordForm.setErrors(err.error);
        },
      });
  }

  public print(): void {
    const content = document.querySelector('.printContent').innerHTML;
    const popupWin = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
    popupWin.document.open();
    popupWin.document.write(`
      <html>
        <head>
          <title>${t('common.backupCodes')}</title>
        </head>
        <body onload="window.print();window.close()">${content}</body>
      </html>`);
    popupWin.document.close();
  }

  public verifyAppCode(): void {
    if (this.codeForm.invalid) {
      return;
    }
    this.isLoading = true;
    this._usersAPI
      .activateMfa(this._currentUser.id, this.mfa.id, {
        totpToken: this.formModel.totpToken,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.isMfaConfirmed = true;
          this.mfa.confirmed = true;
          this._dialogRef.close(this.mfa);
        },
        error: (err) => {
          this.isLoading = false;
          this.codeForm.setErrors(err.error);
        },
      });
  }

  public cancel(): void {
    this._dialogService
      .confirm({
        message: t('account.dialogs.mfa.setup.cancelConfirm.message'),
        confirmButton: {
          text: t('common.leave'),
          color: 'warn',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._dialogRef.close(this.mfa);
      });
  }

  private _genBackupCodes(): void {
    this._usersAPI
      .genBackupCodes(this._currentUser.id, {
        password: this.formModel.password,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((codes) => {
        this.backupCodes = codes;
        this.copyContent = this.backupCodes.tokens.join('\n');
      });
  }

  private _checkBackupCodes(): void {
    this._usersAPI
      .getBackupCodes(this._currentUser.id, {
        password: this.formModel.password,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((codes) => {
        if (codes.tokens?.length) {
          this.backupCodes = codes;
          this.copyContent = this.backupCodes.tokens.join('\n');
        } else {
          this._genBackupCodes();
        }
      });
  }
}
